import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { asyncScheduler, combineLatest, Observable } from 'rxjs';
import { map, take, throttleTime } from 'rxjs/operators';

import { ClearUploadItems } from '@actions/uploads.actions';
import { allUploadItemsSelector, hasUploadsInProgressSelector } from '@state/selectors/uploadItem.selector';

import { UploadSandboxService } from '@common/sandboxes/upload/upload.sandbox.service';
import { DialogHelperBase, OverviewSandbox, Storage } from '@mediahaven/shared';
import {
    FacetSelectionType,
    PaginatedResults,
    PaginationInfo,
    ShFacet,
    ShFacetValue,
    ShSelectableFacetValue,
    ShSelectedFacetValue,
    ShUrlQueryService,
    sortByProperty,
    SortDirection,
    StructuredQuery,
    UploadItem
} from '@stylehaven/components';
import { ApplicationState } from 'state';

export enum UploadItemFacets {
    STATUS = 'Status'
}

@Injectable()
export class UploadsOverviewSandboxService extends OverviewSandbox {
    private uploadSandboxService: UploadSandboxService = inject(UploadSandboxService);
    protected urlQueryService: ShUrlQueryService = inject(ShUrlQueryService);

    private uploads$!: Observable<UploadItem[]>;
    public hasUploadsInProgress: boolean = false;

    // Injectors
    private dialogHelper: DialogHelperBase = inject(DialogHelperBase);

    constructor(private store: Store<ApplicationState>) {
        super();

        this.uploads$ = this.store
            .select(allUploadItemsSelector)
            .pipe(throttleTime(300, asyncScheduler, { leading: false, trailing: true }));

        this.initInProgressUploadsSync();
    }

    private initInProgressUploadsSync(): void {
        this.isUploading().subscribe(isUploading => {
            this.hasUploadsInProgress = isUploading;
        });
    }

    public syncStorage(action: Storage) {
        this.store.dispatch(action);
    }

    public getPaginatedUploads(
        paginationInfo: PaginationInfo,
        structuredQuery: StructuredQuery
    ): Observable<PaginatedResults<UploadItem>> {
        const start: number = paginationInfo.page * paginationInfo.resultsPerPage;
        const end: number = start + paginationInfo.resultsPerPage;
        return this.uploads$.pipe(
            map(uploads => this.filterUploads(uploads, structuredQuery)),
            map(uploads => sortByProperty(uploads, 'date', SortDirection.DESC)),
            map(uploads => {
                const sliced: UploadItem[] = uploads.slice(start, end);

                return {
                    paginationInfo: {
                        ...paginationInfo,
                        totalNumberOfResults: uploads.length
                    },
                    results: sliced
                };
            })
        );
    }

    private filterUploads(uploads: UploadItem[], structuredQuery: StructuredQuery): UploadItem[] {
        let filteredUploads: UploadItem[] = uploads;

        structuredQuery.Facets.forEach(facet => {
            if (facet.DottedKey === UploadItemFacets.STATUS) {
                const statusValues = facet.Buckets?.map(bucket => bucket.Value) || [];
                filteredUploads = filteredUploads.filter(upload => statusValues.includes(upload.state.status));
            }
        });

        return filteredUploads;
    }

    public setSelectedFacetValue(facetValue: ShSelectedFacetValue): void {
        this.urlQueryService.addFacetValue(facetValue);
    }

    public removeSelectedFacetValue(facetValue: ShSelectedFacetValue): void {
        this.urlQueryService.removeFacetValue(facetValue);
    }

    public clearFacets(): void {
        this.urlQueryService.clearFacetQuery();
    }

    public getUploadFacets(): Observable<ShFacet[]> {
        return combineLatest([this.uploads$, this.getUrlQueryState()]).pipe(
            map(([uploads, urlQueryState]) => [
                {
                    key: UploadItemFacets.STATUS,
                    selectionType: FacetSelectionType.SHOULD,
                    label: this.translate('uploads.list.status'),
                    hasMoreValues: false,
                    values: this.uploadsToFacetValues(
                        uploads,
                        urlQueryState.selectedFacets.filter(
                            selectedValue => selectedValue.key === UploadItemFacets.STATUS
                        )
                    )
                }
            ])
        );
    }

    public uploadsToFacetValues(
        uploads: UploadItem[],
        selectedValues: ShSelectedFacetValue[]
    ): ShSelectableFacetValue[] {
        const states: Map<string, number> = new Map();

        uploads.forEach(upload => {
            states.set(upload.state.status, (states.get(upload.state.status) ?? 0) + 1);
        });

        const facetValues: ShFacetValue[] = [];

        states.forEach((count, status) => {
            if (!selectedValues.find(selectedValue => selectedValue.value === status)) {
                facetValues.push({
                    value: status,
                    label: this.translate(`uploads.list.statuses.${status}`, {
                        percentage: 0
                    }).split(' - ')[0],
                    count: count,
                    selectionType: FacetSelectionType.SHOULD
                });
            }
        });

        facetValues.sort((valueA, valueB) => valueB.count - valueA.count);
        return facetValues;
    }

    public isUploading(): Observable<boolean> {
        return this.store.select(hasUploadsInProgressSelector);
    }

    public cancelUpload(uploadItem: UploadItem): void {
        return this.uploadSandboxService.cancelOrAbortUpload(uploadItem);
    }

    public canPauseUpload(uploadItem: UploadItem): boolean {
        return this.uploadSandboxService.canPauseUpload(uploadItem);
    }

    public pauseUpload(uploadItem: UploadItem): void {
        return this.uploadSandboxService.pauseUpload(uploadItem);
    }

    public canResumeUpload(uploadItem: UploadItem): boolean {
        return this.uploadSandboxService.canResumeUpload(uploadItem);
    }

    public resumeUpload(uploadItem: UploadItem): void {
        return this.uploadSandboxService.resumeUpload(uploadItem);
    }

    public onBrowserRefresh(): Observable<void> {
        return this.uploadSandboxService.onBrowserRefresh();
    }

    public clearUploadItems(): void {
        const message: string = this.translateService.translate('dialog.uploads.clear-confirm.message');
        const title: string = this.translateService.translate('dialog.uploads.clear-confirm.title');
        const yes: string = this.translateService.translate('dialog.uploads.clear-confirm.yes');
        const no: string = this.translateService.translate('actions.common.cancel');

        this.dialogHelper
            .confirm({ title, message, yes, no })
            .pipe(take(1))
            .subscribe(() => {
                this.store.dispatch(new ClearUploadItems());
            });
    }
}
